import type { TgvPricingIssue } from '../reducers/transient-state.reducer';
import type {
  PricingIssueAnsweredPayload,
  TgvPricingIssueAnswer,
  TgvPricingIssueKind,
  TgvPricingStatus,
} from '../tgvActions';

export const ANSWER_OF_KIND: Record<TgvPricingIssueKind, TgvPricingIssueAnswer> = {
  failed: 'retry',
  stuck: 'wait',
};

export const keepsPricingIssueKey = (
  kind: TgvPricingIssueKind,
  status: TgvPricingStatus | undefined,
): boolean =>
  kind === 'failed' ? status === 'failed' : status !== 'failed' && status !== 'priced';

export const isAnswerOfKind = (answer: TgvPricingIssueAnswer, kind: TgvPricingIssueKind): boolean =>
  answer === 'leave' || answer === ANSWER_OF_KIND[kind];

export const withoutErrorCodes = (
  errorCodes: Record<string, string>,
  keys: string[],
): Record<string, string> =>
  Object.fromEntries(Object.entries(errorCodes).filter(([key]) => !keys.includes(key)));

export const withoutPricingIssueKeys = (
  issue: TgvPricingIssue,
  keys: string[],
): TgvPricingIssue | null => {
  const remaining = issue.keys.filter(key => !keys.includes(key));
  return remaining.length === 0
    ? null
    : { ...issue, keys: remaining, errorCodes: withoutErrorCodes(issue.errorCodes, keys) };
};

export const answeredKeysOf = (
  issue: Pick<TgvPricingIssue, 'kind' | 'keys'> | null,
  { answer, kind, keys }: PricingIssueAnsweredPayload,
): string[] => {
  if (!issue || issue.kind !== kind || !isAnswerOfKind(answer, kind)) {
    return [];
  }
  return keys === undefined ? issue.keys : issue.keys.filter(key => keys.includes(key));
};

export const withoutKeysOfIssue = (
  issue: TgvPricingIssue | null,
  keys: string[],
): TgvPricingIssue | null =>
  issue && issue.keys.some(key => keys.includes(key))
    ? withoutPricingIssueKeys(issue, keys)
    : issue;

export const withStatusOfKey = (
  issue: TgvPricingIssue | null,
  key: string,
  status: TgvPricingStatus,
): TgvPricingIssue | null =>
  issue && !keepsPricingIssueKey(issue.kind, status) ? withoutKeysOfIssue(issue, [key]) : issue;
